import {
  useEffect,
  useState,
  MouseEvent,
  Dispatch,
  SetStateAction,
  useRef,
} from "react";
import {
  BsCaretDownFill,
  BsFillCheckSquareFill,
  BsSortDown,
  BsSortUp,
  BsThreeDotsVertical,
} from "react-icons/bs";
import { PiCaretRightBold } from "react-icons/pi";
import { usePositioning } from "@/hooks/usePositioning";
import { ButtonWrapper } from "../../../pages/register/style";
import { IFilterInput, KeyValueObj } from "@/common/types";
import { validateData } from "@/common/utils/rules";
import { ClipSpinner } from "@/common/components/spinner";
import { TableContainer, TableWrapper, TR } from "./subs/style";
import { Button } from "@/common/elements";
import { GiSettingsKnobs } from "react-icons/gi";
import { PiCaretDownBold } from "react-icons/pi";
import { useStore } from "@/hooks/useContexts";
import { TableModals } from "./subs/modals";
import { TableLoader } from "./subs/TableLoader";
import { ActionPopup, Options } from "@/common/components/action-popup";
import { EmptyTable } from "./subs/EmptyTable";
import { ErrorLoading } from "./subs/ErrorLoading";

let timer: any;
interface IDropDownOptions {
  label: string;
  action: (row: KeyValueObj) => void;
}

// MED-719 — a sortable list opts in by passing `sortFields`; every other
// caller of this shared Table gets no prop, renders no control, and is
// therefore unaffected by this addition.
//
// MED-719 follow-up: field and direction were originally combined into one
// list of options ("Name (A-Z)", "Price (high to low)", ...). Team feedback
// was that this doesn't read as sorting UI — the standard convention is a
// field picker plus a separate direction toggle, so that's what this is now.
export interface ISortField {
  label: string;
  sortBy: string;
}

interface TableProps {
  tableHeaders: (string | JSX.Element)[];
  tableData: KeyValueObj[];
  actionsData?: any[];
  tableKeys?: string[];
  full?: boolean;
  spaced?: boolean;
  headerBg?: string;
  lined?: boolean;
  rowLineColor?: string;
  withCheck?: boolean;
  isRadio?: boolean;
  // When set (with withCheck/isRadio), clicking anywhere on a row toggles its
  // selection, not just the checkbox cell. Off by default so existing tables
  // are unaffected.
  selectRowOnClick?: boolean;
  shouldHover?: boolean;
  isForm?: boolean;
  submitLabel?: string;
  submitLoading?: boolean;
  clickable?: boolean;
  onTDClick?: (rowId: string | number, columnId: string | number) => void;
  onTRClick?: (id: string | number) => void;
  selectedList?: KeyValueObj[];
  setSelectedList?: Dispatch<SetStateAction<KeyValueObj[]>>;
  onFormSubmit?: (formValues: KeyValueObj) => void;
  onMouseEnter?: (e: MouseEvent<HTMLElement>, rowId: string) => void;
  onMouseLeave?: (e: MouseEvent<HTMLElement>, rowId: string) => void;
  onMouseInnerEnter?: (e: MouseEvent<HTMLElement>, rowId: string) => void;
  onMouseInnerLeave?: (e: MouseEvent<HTMLElement>, rowId: string) => void;
  filterInputs?: IFilterInput[];
  handleFilter?: (values: KeyValueObj) => void;
  // Optional: the caller's currently-applied filter values, so reopening
  // the filter modal shows what's active instead of resetting to blank.
  // Omit it and behavior is unchanged — Filter's initialValues stays
  // undefined, same as before this existed.
  filterValues?: KeyValueObj;
  tableTitle?: string;
  tableSubtitle?: string;
  handleSearch?: (searchString: string) => void;
  loading?: boolean;
  error?: boolean;
  handleRetryLoading?: () => void;
  dropDownOptions?: IDropDownOptions[];
  showDropDown?: boolean;
  handleDropDownClicked?: (row: KeyValueObj) => void;
  removeFilters?: boolean;
  errorLoading?: boolean;
  isSearching?: boolean;
  onRetry?: () => void;
  headerAction?: { label: string; action: () => void };
  // MED-719: optional sort control, rendered in the same controls row as
  // search/filter only when provided. `activeSortBy`/`activeSortDirection`
  // are optional too — omit them and the control defaults to the first
  // field, ascending, matching the list's own server-side default.
  sortFields?: ISortField[];
  activeSortBy?: string;
  activeSortDirection?: "asc" | "desc";
  onSort?: (sortBy: string, direction: "asc" | "desc") => void;
}

const Table = ({
  filterInputs,
  filterValues,
  tableHeaders,
  tableData,
  tableKeys,
  full,
  spaced,
  headerBg,
  withCheck,
  isRadio,
  selectRowOnClick,
  onTDClick,
  onTRClick,
  selectedList,
  setSelectedList,
  lined,
  rowLineColor,
  shouldHover,
  isForm,
  submitLabel,
  submitLoading,
  onFormSubmit,
  clickable,
  // @ts-ignore
  onMouseInnerLeave,
  // @ts-ignore
  onMouseInnerEnter,
  handleFilter,
  tableTitle,
  tableSubtitle,
  loading,
  // @ts-ignore
  error,
  // @ts-ignore
  handleRetryLoading,
  handleSearch,
  dropDownOptions,
  showDropDown,
  handleDropDownClicked,
  removeFilters,
  errorLoading,
  isSearching,
  onRetry,
  headerAction,
  sortFields,
  activeSortBy,
  activeSortDirection,
  onSort,
}: TableProps) => {
  function onTDButtonClicked(e: any, callBack: any, rowId: string) {
    e.stopPropagation();
    callBack(e, rowId);
  }

  const [formValues, setFormValues] = useState<KeyValueObj>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [validations, setValidations] = useState<KeyValueObj>({});
  const [, setSelectOptions] = useState<KeyValueObj[]>([]);
  const [, setShowOptions] = useState(false);
  const [, setSelectType] = useState("");

  const [loadingStates, setLoadingStates] = useState<any>({});
  const [errorMessages, setErrorMessages] = useState<any>({});
  const [, setCurrentId] = useState("");
  const [, setCurrentName] = useState("");

  const [currentActionId, setCurrentActionId] = useState("");

  //Hookes

  const [setPosition] = usePositioning();
  const { setCurrentLevel2Modal, setLevel2ModalOptions } = useStore();

  // MED-719 — the sort control's own popup, independent of the filter modal
  // above and the per-row action popups below.
  const [setSortPosition, sortPosition] = usePositioning();
  const [sortOpen, setSortOpen] = useState(false);

  const tableBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //set up all validations and initial loading states of the checkbox
    const tempValidations: KeyValueObj = {};
    const tempLoadingStates: KeyValueObj = {};
    const tempFormValues: KeyValueObj = {};
    const tempErrorMessages: KeyValueObj = {};

    tableData?.forEach((input) => {
      const currentIdInner = input?.id;
      Object.keys(input).forEach((objKey: string) => {
        if (input[objKey]?.type === "input") {
          tempValidations[input[objKey]?.name + "_" + currentIdInner] = false;
          tempLoadingStates[currentIdInner] = {
            ...tempLoadingStates[currentIdInner],
            [input[objKey]?.name]: false,
          };
          tempFormValues[currentIdInner] = {
            ...tempFormValues[currentIdInner],
            [input[objKey].name]:
              input[objKey].inputType === "checkBox" ? [] : "",
          };
          tempErrorMessages[currentIdInner] = {
            ...tempErrorMessages[currentIdInner],
            [input[objKey].name]: [],
          };
        }
      });

      //tempValidations[input.name] = false;
    });
    setValidations(tempValidations);
    setLoadingStates(tempLoadingStates);
    setFormValues(tempFormValues);
    setErrorMessages(tempErrorMessages);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  //setting the validity of the form
  useEffect(() => {
    setIsFormValid(true);
    //go through all validations and set isFormValid to false if any of the
    //validation is false
    Object.values(validations).forEach((validation: boolean) => {
      if (validation === false) {
        setIsFormValid(false);
        return;
      }
    });
  }, [validations]);

  useEffect(() => {
    clearTimeout(timer);
  }, []);

  async function onSelectClicked(
    e: any,
    id: string,
    name: string,
    options: any,
    selectType: string,
    fetchOptions?: any,
  ) {
    e.stopPropagation();
    setPosition(e);
    setCurrentId(id);
    setCurrentName(name);
    setShowOptions(true);
    setSelectType(selectType);

    //set the options
    if (fetchOptions) {
      //set loading state
      setLoadingStates({
        ...loadingStates,
        [id]: {
          ...loadingStates[id],
          [name]: true,
        },
      });

      //fetch the data with the fetch function
      const fetchedOptions = await fetchOptions(id);
      setSelectOptions(fetchedOptions);

      //clear loading state
      setLoadingStates({
        ...loadingStates,
        [id]: {
          ...loadingStates[id],
          [name]: false,
        },
      });
    } else {
      setSelectOptions(options);
    }
  }

  function onFilter(values: KeyValueObj) {
    setCurrentLevel2Modal("");
    handleFilter?.(values);
  }

  function handleFilterClick() {
    setLevel2ModalOptions({
      filterInputs,
      onFilter,
      filterValues,
    });
    filterInputs && setCurrentLevel2Modal("table-filters");
  }

  function onActionClick(data: KeyValueObj) {
    setCurrentActionId((prev: string) => (prev === data?.id ? "" : data?.id));
    handleDropDownClicked && handleDropDownClicked(data);
  }

  function onSortClick(e: MouseEvent<HTMLElement>) {
    setSortPosition(e);
    setSortOpen(true);
  }

  // Undefined until the caller has an explicit selection — default to the
  // first field, ascending, which is also the list's own server-side
  // default, so the control's resting state always matches reality.
  const currentSortBy = activeSortBy ?? sortFields?.[0]?.sortBy;
  const currentSortDirection = activeSortDirection ?? "asc";
  const currentSortField = sortFields?.find(
    (field) => field.sortBy === currentSortBy,
  );

  const sortActions = (sortFields ?? []).map((field) => ({
    label: field.label,
    action: () => {
      onSort?.(field.sortBy, currentSortDirection);
      setSortOpen(false);
    },
  }));

  function onToggleSortDirection() {
    if (!currentSortBy) return;
    onSort?.(currentSortBy, currentSortDirection === "asc" ? "desc" : "asc");
  }

  function onInputChange(e: any, id: string, name: string, rules: any) {
    e.stopPropagation();
    const value = e.target.value;
    //check for validation
    const { isValid, errorMessages: obtainedError } = validateData(
      value,
      rules,
      formValues[id],
    );

    //update validations object
    setValidations({
      ...validations,
      [name + "_" + id]: isValid,
    });

    //set errorMessage
    setErrorMessages({
      ...errorMessages,
      [id]: {
        ...errorMessages[id],
        [name]: obtainedError,
      },
    });

    //set value if it's valid
    if (isValid) {
      setFormValues({
        ...formValues,
        [id]: {
          ...formValues[id],
          [name]: value,
        },
      });
    }
  }

  function onSearch(e: any) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      handleSearch?.(e.target.value);
    }, 500);
  }
  function onCheckClicked(row: KeyValueObj) {
    //the table row id will be added to the list if it does not already exist
    const exists = selectedList?.find(
      (distributor: KeyValueObj) => distributor.id === row.id,
    );
    if (!exists) {
      isRadio
        ? setSelectedList?.([row])
        : setSelectedList?.((prev: KeyValueObj[]) => [...prev, row]);
    } else {
      setSelectedList?.((prev: KeyValueObj[]) => {
        prev = prev.filter(
          (distributor: KeyValueObj) => distributor.id !== row.id,
        );
        return prev.slice();
      });
    }
  }

  // MED-708: with a paginated table `selectedList` can hold rows that are not
  // on the page in view, so "is everything selected?" has to be asked of the
  // rows in front of the user, by id. Comparing the two lengths read "25
  // selected, 25 rows shown" as all-selected and cleared the other pages
  // along with them. For a table that never pages, prev is a subset of
  // `tableData` and this behaves exactly as before.
  function isPageFullySelected(rows: KeyValueObj[]) {
    return (
      (rows?.length ?? 0) > 0 &&
      rows.every((row: KeyValueObj) =>
        selectedList?.some((selected: KeyValueObj) => selected.id === row.id),
      )
    );
  }

  function onCheckAllClicked(tableData: KeyValueObj[]) {
    if (isRadio) return;
    if (isPageFullySelected(tableData)) {
      setSelectedList?.((prev: KeyValueObj[]) =>
        prev.filter(
          (selected: KeyValueObj) =>
            !tableData.some((row: KeyValueObj) => row.id === selected.id),
        ),
      );
    } else {
      setSelectedList?.((prev: KeyValueObj[]) => [
        ...prev,
        ...tableData.filter(
          (row: KeyValueObj) =>
            !prev.some((selected: KeyValueObj) => selected.id === row.id),
        ),
      ]);
    }
  }

  let totalCols = tableKeys?.length || 0;
  if (withCheck || isRadio) {
    totalCols += 1;
  }
  if ((dropDownOptions?.length as number) > 0) {
    totalCols += 1;
  }

  function scrollBodyBy(value: number) {
    tableBodyRef?.current?.scrollBy({ top: value, behavior: "smooth" });
  }

  return (
    <>
      <TableModals />

      <TableWrapper>
        {(tableTitle?.length as number) > 0 ||
        (tableSubtitle?.length as number) > 0 ? (
          <div className="title-wrapper">
            {(tableTitle?.length as number) > 0 ? (
              <h2 className="title">{tableTitle}</h2>
            ) : null}
            {(tableSubtitle?.length as number) > 0 ? (
              <h3 className="dash-text">{tableSubtitle}</h3>
            ) : null}
          </div>
        ) : null}

        <TableContainer
          headerBg={headerBg}
          full={full}
          allSelected={isPageFullySelected(tableData)}
        >
          {removeFilters ? null : (
            <div className="table-controls">
              <div className="flex-row search-wrapper">
                <input
                  className="search-input"
                  type="text"
                  placeholder="&#61442;   Search"
                  onChange={onSearch}
                />
                <div
                  onClick={handleFilterClick}
                  className="flex-row search-filter"
                >
                  <GiSettingsKnobs className="filter-icon" />
                  <h3>Filter by</h3>
                </div>
                {sortFields && sortFields.length > 0 ? (
                  <div className="flex-row sort-wrapper">
                    <div
                      onClick={onSortClick}
                      className="flex-row search-filter"
                    >
                      <h3>Sort by: {currentSortField?.label ?? "—"}</h3>
                      <PiCaretDownBold className="sort-caret-icon" />
                      <ActionPopup
                        position={sortPosition}
                        open={sortOpen}
                        actions={sortActions}
                        onClose={() => setSortOpen(false)}
                        width="14rem"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={onToggleSortDirection}
                      className="sort-direction-toggle"
                      title={
                        currentSortDirection === "desc"
                          ? "Sorted descending — click for ascending"
                          : "Sorted ascending — click for descending"
                      }
                      aria-label={
                        currentSortDirection === "desc"
                          ? "Sorted descending, click to sort ascending"
                          : "Sorted ascending, click to sort descending"
                      }
                    >
                      {currentSortDirection === "desc" ? (
                        <BsSortDown />
                      ) : (
                        <BsSortUp />
                      )}
                    </button>
                  </div>
                ) : null}
                {headerAction ? (
                  <div className="header-action-wrapper">
                    <Button secondary onClick={headerAction?.action}>
                      {headerAction?.label}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          <div ref={tableBodyRef} className="table-scroll-wrapper">
            <table>
              <thead>
                <tr>
                  {tableHeaders ? (
                    <>
                      {withCheck || isRadio ? (
                        <th>
                          <BsFillCheckSquareFill
                            onClick={() => onCheckAllClicked?.(tableData)}
                            className="check-icon header-check"
                          />
                        </th>
                      ) : null}

                      {tableHeaders.map(
                        (header: string | JSX.Element, i: number) => (
                          <th key={i}>{header}</th>
                        ),
                      )}
                      {dropDownOptions && dropDownOptions?.length > 0 ? (
                        <th>Action</th>
                      ) : null}
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {!loading && tableData?.length === 0 && !errorLoading ? (
                  <EmptyTable
                    isSearching={!!isSearching}
                    colSize={tableHeaders.length}
                  />
                ) : null}
                {!loading && tableData?.length === 0 && errorLoading ? (
                  <ErrorLoading
                    onRetry={() => onRetry?.()}
                    colSize={tableHeaders?.length}
                  />
                ) : null}
                {loading ? (
                  <TableLoader columnSize={totalCols} />
                ) : (
                  tableData?.map((data, i) => {
                    return (
                      <TR
                        full={full}
                        lined={lined}
                        rowLineColor={rowLineColor}
                        shouldHover={shouldHover}
                        spaced={spaced}
                        selected={
                          selectedList &&
                          selectedList.findIndex(
                            (list) => list.id === data.id,
                          ) !== -1
                        }
                        key={i}
                        onClick={() => {
                          onTRClick && onTRClick(data.id);
                          if (selectRowOnClick && (withCheck || isRadio)) {
                            onCheckClicked?.(data);
                          }
                        }}
                        indexValue={9999 - i}
                      >
                        {withCheck || isRadio ? (
                          <td
                            onClick={(e) => {
                              // Stop the bubble so the row handler doesn't
                              // toggle a second time and cancel this one out.
                              if (selectRowOnClick) e.stopPropagation();
                              onCheckClicked?.(data);
                            }}
                          >
                            <BsFillCheckSquareFill className="check-icon" />
                          </td>
                        ) : null}
                        {tableKeys?.map((theKey: string, i: number) => (
                          <td
                            style={data[theKey]?.style ?? {}}
                            key={theKey}
                            onClick={() => onTDClick && onTDClick(data.id, i)}
                            onMouseEnter={(e: MouseEvent<HTMLElement>) =>
                              data[theKey]?.onMouseEnter &&
                              data[theKey]?.onMouseEnter(e, data.id)
                            }
                            onMouseLeave={(e: MouseEvent<HTMLElement>) =>
                              data[theKey]?.onMouseLeave &&
                              data[theKey]?.onMouseLeave(e, data.id)
                            }
                          >
                            {data[theKey]?.type === "string" &&
                              data[theKey]?.value}
                            {data[theKey]?.type === "image" && (
                              <img
                                className="table-image"
                                src={data[theKey]?.value}
                                alt={theKey}
                              />
                            )}
                            {data[theKey]?.type === "button" &&
                              (data[theKey]?.buttonType === "dots" ? (
                                <button
                                  disabled={data[theKey]?.buttonLoading}
                                  onClick={(e: any) =>
                                    onTDButtonClicked(
                                      e,
                                      data[theKey]?.onClick,
                                      data.id,
                                    )
                                  }
                                  className="dotted-button"
                                >
                                  {data[theKey]?.buttonLoading ? (
                                    "Processing..."
                                  ) : (
                                    <BsThreeDotsVertical className="button-icon" />
                                  )}
                                </button>
                              ) : data[theKey]?.buttonType === "secondary" ? (
                                <Button
                                  width="6rem"
                                  secondary
                                  onClick={(e: any) =>
                                    onTDButtonClicked(
                                      e,
                                      data[theKey]?.onClick,
                                      data.id,
                                    )
                                  }
                                  disabled={data[theKey]?.buttonLoading}
                                >
                                  {data[theKey]?.buttonName}
                                </Button>
                              ) : data[theKey]?.buttonType === "danger" ? (
                                <button
                                  className={
                                    data[theKey]?.buttonType + " danger-button"
                                  }
                                  onClick={(e: any) =>
                                    onTDButtonClicked(
                                      e,
                                      data[theKey]?.onClick,
                                      data.id,
                                    )
                                  }
                                  disabled={data[theKey]?.buttonLoading}
                                >
                                  {data[theKey]?.buttonName}
                                </button>
                              ) : (
                                <button
                                  className={
                                    data[theKey]?.buttonType + " regular-button"
                                  }
                                  onClick={(e: any) =>
                                    onTDButtonClicked(
                                      e,
                                      data[theKey]?.onClick,
                                      data.id,
                                    )
                                  }
                                >
                                  {data[theKey]?.buttonName}
                                </button>
                              ))}
                            {data[theKey]?.type === "status" && (
                              <div
                                onMouseEnter={(e: MouseEvent<HTMLElement>) =>
                                  data[theKey]?.onMouseInnerEnter &&
                                  data[theKey]?.onMouseInnerEnter(e, data.id)
                                }
                                onMouseLeave={(e: MouseEvent<HTMLElement>) =>
                                  data[theKey]?.onMouseInnerLeave &&
                                  data[theKey]?.onMouseInnerLeave(e, data.id)
                                }
                                className={`status ${
                                  data[theKey]?.statusType ??
                                  data?.[theKey]?.value
                                }`}
                              >
                                <div
                                  className={`status-inner ${
                                    data[theKey]?.statusType ??
                                    data?.[theKey]?.value
                                  }`}
                                >
                                  <div className="status-dot"></div>
                                  <h3>{data[theKey]?.value}</h3>
                                </div>
                              </div>
                            )}
                            {data[theKey]?.type === "input" && (
                              <div className="input-wrapper">
                                {data[theKey]?.inputType === "checkBox" ? (
                                  <label
                                    onClick={(e: any) =>
                                      onSelectClicked(
                                        e,
                                        data.id,
                                        data[theKey]?.name,
                                        data[theKey]?.options,
                                        "checkBox",
                                        data[theKey]?.fetchOptions,
                                      )
                                    }
                                  >
                                    <span>
                                      {formValues[data.id] &&
                                      formValues[data.id][data[theKey]?.name]
                                        .length > 0 ? (
                                        <span>
                                          Selected{" "}
                                          <BsFillCheckSquareFill className="selected-icon" />
                                        </span>
                                      ) : (
                                        data[theKey]?.placeholder
                                      )}
                                    </span>
                                    <span>
                                      <BsCaretDownFill />
                                    </span>
                                  </label>
                                ) : data[theKey]?.inputType === "select" ? (
                                  <label
                                    onClick={(e: any) =>
                                      onSelectClicked(
                                        e,
                                        data.id,
                                        data[theKey]?.name,
                                        data[theKey]?.options,
                                        "select",
                                      )
                                    }
                                  >
                                    <span>
                                      {formValues[data.id] &&
                                      formValues[data.id][data[theKey]?.name]
                                        ? formValues[data.id][
                                            data[theKey]?.name
                                          ]
                                        : data[theKey]?.placeholder}
                                    </span>
                                    <span>
                                      <BsCaretDownFill />
                                    </span>
                                  </label>
                                ) : (
                                  <>
                                    <input
                                      onChange={(e) =>
                                        onInputChange(
                                          e,
                                          data.id,
                                          data[theKey]?.name,
                                          data[theKey]?.rules,
                                        )
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      type={data[theKey]?.inputType}
                                    />
                                    {errorMessages[data.id] && (
                                      <h3 className="error-message">
                                        {
                                          errorMessages[data.id][
                                            data[theKey]?.name
                                          ]
                                        }
                                      </h3>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        ))}
                        {dropDownOptions && dropDownOptions?.length > 0 ? (
                          <div className="actions-wrapper">
                            <td>
                              <button
                                onClick={(e: any) => {
                                  // MED-677: without this, the click bubbles to the
                                  // row's onTRClick and navigates away before the
                                  // dropdown ever gets a chance to render.
                                  e.stopPropagation();
                                  onActionClick(data);
                                }}
                                className="dotted-button"
                              >
                                <BsThreeDotsVertical className="button-icon" />
                              </button>

                              {currentActionId === data?.id && showDropDown ? (
                                <>
                                  {/* <Backdrop
                                    backdropClicked={() =>
                                      setCurrentActionId("")
                                    }
                                  /> */}
                                  <div className="options-wrapper">
                                    <Options
                                      callActionWith={data}
                                      actions={dropDownOptions}
                                      scrollLastToView
                                      scrollParentBy={scrollBodyBy}
                                    />
                                  </div>
                                </>
                              ) : null}
                            </td>
                          </div>
                        ) : null}

                        {clickable ? (
                          <td>
                            <PiCaretRightBold className="click-icon" />
                          </td>
                        ) : null}
                      </TR>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </TableContainer>

        {isForm && (
          <ButtonWrapper>
            <Button
              onClick={() => onFormSubmit && onFormSubmit(formValues)}
              disabled={!isFormValid || submitLoading}
            >
              {submitLoading ? (
                <ClipSpinner />
              ) : submitLabel ? (
                submitLabel
              ) : (
                "Continue"
              )}
            </Button>
          </ButtonWrapper>
        )}
      </TableWrapper>
    </>
  );
};

export default Table;
